import { useCreateSolarDesign } from '@/api/mutations/project/useCreateSolarDesign';
import type { ProjectSolarPotential } from '@/api/queries/project/useGetProjectSolarPotential';
import { useSolarDesignDrafts } from '@/api/queries/project/useSolarDesignDrafts';
import { useSolarDesignerState } from '@/api/queries/project/useSolarDesignerState';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  AnchorButton,
  Button,
  Field,
  Form,
  H4,
  Modal,
  RawInput,
  SkeletonLoader,
  Textarea,
  Text,
} from '@payaca/ambrosia';
import {
  SolarRoofView,
  type SolarLatLng,
} from '@payaca/components/solarRoofView/SolarRoofView';
import { solarDesignDraftStateSchema } from '@payaca/helpers/solarDesignDraft';
import { solarDesignRegistersToAnalysedImagery } from '@payaca/helpers/solarPackLayout';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import { z } from 'zod/v4';
import { manualDesignSource } from '../../../solarDesignerPage/solarDesigner/manualDesignSource';
import SolarDesignEquipment from './SolarDesignEquipment';
import { manualSolarPreview, useSolarPreview } from './solarPreview';

const formSchema = z.object({
  name: z.string().trim().min(1, 'Enter a design name').max(120),
  description: z.string().max(2000),
});
const CreateDesignModal = ({
  projectId,
  projectPublicId,
  source,
  designId,
  isOpen,
  onClose,
}: {
  projectId: string;
  projectPublicId?: string;
  source: ProjectSolarPotential;
  designId: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const history = useHistory();
  const create = useCreateSolarDesign(projectId, projectPublicId);
  const methods = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });
  const { reset } = methods;
  useEffect(() => {
    if (isOpen) reset({ name: '', description: '' });
  }, [isOpen, reset]);
  return (
    <Modal
      isOpen={isOpen}
      title="New solar design"
      onClose={() => {
        if (!create.isPending) onClose();
      }}
    >
      <Form
        methods={methods}
        onSubmit={async (values) => {
          try {
            const result = await create.mutateAsync({
              ...values,
              designId,
              source,
            });
            history.push(
              `/projects/${projectId}/solar-designer?design=${result.saveSolarDesignDraft.id}`
            );
            onClose();
          } catch {
            /* The mutation error remains visible in the form. */
          }
        }}
      >
        <Modal.Body className="space-y-4">
          <Field name="name">
            <Field.Label>Design name</Field.Label>
            <RawInput
              {...methods.register('name')}
              autoFocus
              maxLength={120}
              placeholder="e.g. South roof with battery"
            />
          </Field>
          <Field name="description">
            <Field.Label>Description (optional)</Field.Label>
            <Controller
              name="description"
              control={methods.control}
              render={({ field }) => (
                <Textarea
                  value={field.value}
                  onChange={field.onChange}
                  rows={3}
                  maxLength={2000}
                />
              )}
            />
          </Field>
          {create.isError && (
            <Text as="div" role="alert">
              The design could not be created. Please try again.
            </Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" isProcessing={create.isPending}>
              Create design
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const SolarDesignDraftPreview = ({
  projectId,
  designId,
  currentSource,
  siteAddressCentre,
}: {
  projectId: string;
  designId: string;
  currentSource?: ProjectSolarPotential | null;
  siteAddressCentre?: SolarLatLng;
}) => {
  const designerState = useSolarDesignerState(projectId, true, designId);
  const queriedDraft = designerState.data?.projectById.solarDesignDraft;
  const draft = queriedDraft?.id === designId ? queriedDraft : undefined;
  const isManual = draft?.basis === 'MANUAL';
  const matchingCurrentSource =
    currentSource &&
    draft?.sourceAnalysisId === currentSource.sourceAnalysisId &&
    draft.sourceAnalysisVersion === currentSource.sourceAnalysisVersion
      ? currentSource
      : undefined;
  const source = isManual
    ? undefined
    : (draft?.analysis ?? matchingCurrentSource);
  const hasValidState = solarDesignDraftStateSchema.safeParse(
    draft?.state
  ).success;
  const preview = useSolarPreview(source, draft);
  const manualPreview = useMemo(
    () => (isManual ? manualSolarPreview(draft?.state) : undefined),
    [isManual, draft?.state]
  );
  const manualSource = useMemo(
    () =>
      isManual && draft && siteAddressCentre
        ? manualDesignSource(siteAddressCentre, draft)
        : undefined,
    [isManual, draft, siteAddressCentre]
  );
  const rgbOverlay = source?.rgbOverlay;
  const rgbResourceKey = `${source?.sourceAnalysisId}:${source?.sourceAnalysisVersion}:rgb`;
  const rgbUnderlay = useMemo(
    () =>
      rgbOverlay
        ? {
            url: rgbOverlay.url,
            resourceKey: rgbResourceKey,
            bounds: {
              sw: { lat: rgbOverlay.bounds.south, lng: rgbOverlay.bounds.west },
              ne: { lat: rgbOverlay.bounds.north, lng: rgbOverlay.bounds.east },
            },
          }
        : undefined,
    [rgbOverlay, rgbResourceKey]
  );

  let content;
  if (designerState.isPending || (!draft && designerState.isFetching)) {
    content = <SkeletonLoader className="h-64 w-full rounded-lg" />;
  } else if (designerState.isError) {
    content = (
      <Text as="div" role="alert">
        This design could not be loaded.
      </Text>
    );
  } else if (!draft) {
    content = <Text>This design is no longer available.</Text>;
  } else if (draft.isStale) {
    content = (
      <Text>
        {isManual
          ? 'This design was drawn for a different site address. Open the designer to review it.'
          : 'This design uses an older roof analysis. Open the designer to review it against the current analysis.'}
      </Text>
    );
  } else if (isManual) {
    content =
      manualPreview && manualSource ? (
        <>
          <div className="w-full overflow-hidden rounded-lg">
            <SolarRoofView
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY ?? ''}
              centre={manualSource.centre!}
              panels={[]}
              roofSegments={[]}
              panelWidthMeters={1.045}
              panelHeightMeters={1.879}
              {...manualPreview}
              heightPx={260}
            />
          </div>
          <SolarDesignEquipment
            projectId={projectId}
            source={manualSource}
            draft={draft}
          />
        </>
      ) : (
        <Text>The saved design preview is unavailable.</Text>
      );
  } else if (!hasValidState || !source?.available || !source.centre) {
    content = <Text>The saved design preview is unavailable.</Text>;
  } else {
    content = (
      <>
        <div className="w-full overflow-hidden rounded-lg">
          <SolarRoofView
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY ?? ''}
            centre={source.centre}
            panels={source.panels}
            roofSegments={source.roofSegments}
            panelWidthMeters={source.panelWidthMeters ?? 1.045}
            panelHeightMeters={source.panelHeightMeters ?? 1.879}
            {...preview}
            rgbUnderlay={rgbUnderlay}
            showRgbUnderlay={!!rgbUnderlay}
            underlayAtEveryZoom={solarDesignRegistersToAnalysedImagery(
              source.geometry
            )}
            heightPx={260}
          />
        </div>
        <SolarDesignEquipment
          projectId={projectId}
          source={source}
          draft={draft}
        />
      </>
    );
  }

  return <div className="space-y-4">{content}</div>;
};

/**
 * The project's saved designs. `source` is the current roof analysis new
 * designs start from; without one (no satellite coverage) the list still shows
 * and opens hand-drawn designs, and the card offers drawing instead.
 */
export const SolarDesignDraftList = ({
  projectId,
  projectPublicId,
  source,
  siteAddressCentre,
  canCreate,
}: {
  projectId: string;
  projectPublicId?: string;
  source?: ProjectSolarPotential | null;
  siteAddressCentre?: SolarLatLng;
  /** False while the account has no preferred panel or inverter to design with. */
  canCreate?: boolean;
}) => {
  const designs = useSolarDesignDrafts(projectId);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [designId, setDesignId] = useState(() => crypto.randomUUID());
  const [selectedDesignId, setSelectedDesignId] = useState<string>();
  const hasInitialSelection = useRef(false);
  const isLoaded = !designs.isPending && !designs.isError;
  const rows = designs.data?.projectById.solarDesignDrafts ?? [];
  const newestDesignId = rows.at(-1)?.id;

  useEffect(() => {
    if (!isLoaded || hasInitialSelection.current) return;

    // The API orders drafts by createdAt then id, ascending. Seed from the
    // final row once; background polling must not move the user's selection.
    hasInitialSelection.current = true;
    setSelectedDesignId(newestDesignId);
  }, [isLoaded, newestDesignId]);

  return (
    <section
      className="space-y-3 border-t border-gray-200 pt-4"
      aria-label="Solar designs"
    >
      <div className="flex items-center justify-between gap-3">
        <H4>Design drafts</H4>
        {source && (
          <Button
            variant="secondary"
            size="sm"
            disabled={canCreate === false}
            onClick={() => {
              setDesignId(crypto.randomUUID());
              setIsCreateModalOpen(true);
            }}
          >
            New design
          </Button>
        )}
      </div>
      {designs.isPending && <Text level="supporting">Loading designs…</Text>}
      {designs.isError && (
        <Text as="div" role="alert">
          Designs could not be loaded.{' '}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void designs.refetch()}
          >
            Try again
          </Button>
        </Text>
      )}
      {isLoaded && rows.length === 0 && (
        <Text level="supporting">
          Create a draft to explore and compare layouts.
        </Text>
      )}
      {isLoaded && rows.length > 0 && (
        <ul className="m-0 list-none divide-y divide-gray-200 border-t border-gray-200 p-0">
          {rows.toReversed().map((design) => (
            <li className="py-3" key={design.id}>
              <Accordion
                open={selectedDesignId === design.id}
                onOpenChange={(open) =>
                  setSelectedDesignId(open ? design.id : undefined)
                }
                title={
                  <div className="min-w-0 grow break-words">
                    <Text as="div">{design.name}</Text>
                    {design.description && (
                      <div className="line-clamp-2 whitespace-pre-wrap break-words">
                        <Text as="div" level="supporting">
                          {design.description}
                        </Text>
                      </div>
                    )}
                    {design.basis === 'MANUAL' && (
                      <Text as="div" level="supporting">
                        Drawn by hand
                      </Text>
                    )}
                    {design.isStale && (
                      <Text as="div" level="supporting">
                        {design.basis === 'MANUAL'
                          ? 'Drawn for a different site address'
                          : 'Uses an older roof analysis'}
                      </Text>
                    )}
                  </div>
                }
                chevronPosition="left"
                panelClassName="pt-3"
                actions={
                  selectedDesignId === design.id ? (
                    <AnchorButton
                      as={Link}
                      to={`/projects/${projectId}/solar-designer?design=${design.id}`}
                      size="sm"
                    >
                      Open designer
                    </AnchorButton>
                  ) : undefined
                }
              >
                {selectedDesignId === design.id && (
                  <SolarDesignDraftPreview
                    projectId={projectId}
                    designId={design.id}
                    currentSource={source}
                    siteAddressCentre={siteAddressCentre}
                  />
                )}
              </Accordion>
            </li>
          ))}
        </ul>
      )}
      {source && (
        <CreateDesignModal
          projectId={projectId}
          projectPublicId={projectPublicId}
          source={source}
          designId={designId}
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />
      )}
    </section>
  );
};
